import {
  FamilyMonthlySipMember,
  FamilyMonthlySipOverview,
  FamilyMonthlySipSummary,
} from "creso-core/types";
import { convertToCurrency, formatAsCrore } from "creso-core/utils";
import { useMemo } from "react";
import { cn, ScrollArea, Separator } from "web-design-system";
import { MonthlySipEmptyStateIcon } from "@core/components/icon/monthly-sip-empty-state";
import { MemberBar } from "./member-bar";
import {
  BreakdownColumn,
  BreakdownTable,
  getColumnAlignClass,
  GRID_TEMPLATE_CLASS,
  MetricValue,
} from "./common";

function getSipBreakdownColumns(
  summary?: FamilyMonthlySipSummary
): BreakdownColumn<FamilyMonthlySipMember>[] {
  const totalMonthlySip = summary?.monthly_sip ?? 0;

  return [
    {
      id: "member",
      render: (row) => (
        <MemberBar
          client_id={row.client_id}
          name={row.display_name}
          holdingPattern={row.holding_pattern}
          value={convertToCurrency(row.monthly_sip)}
          percentage={
            totalMonthlySip > 0 ? (row.monthly_sip / totalMonthlySip) * 100 : 0
          }
        />
      ),
    },
    {
      id: "cumulative_sip",
      render: (row) => (
        <span className="text-sm font-semibold">
          {convertToCurrency(row.cumulative_invested)}
        </span>
      ),
    },
  ];
}

export function MonthlySipSummaryCard({
  overview,
  isLoading,
}: {
  overview?: FamilyMonthlySipOverview;
  isLoading: boolean;
}) {
  const summary = overview?.summary;
  const members = overview?.members ?? [];

  const metrics = useMemo(() => {
    return [
      {
        label: "Total Monthly",
        value: (
          <span className="font-semibold">
            {convertToCurrency(summary?.monthly_sip ?? 0)}
          </span>
        ),
      },
      {
        label: "Cumulative",
        value: (
          <span className="font-semibold">
            {formatAsCrore(summary?.cumulative_invested ?? 0, 1)}
          </span>
        ),
      },
    ];
  }, [summary]);

  const breakdownColumns = useMemo(
    () => getSipBreakdownColumns(summary),
    [summary]
  );
  const totalColumns = metrics.length;
  const gridClassName =
    GRID_TEMPLATE_CLASS[totalColumns] ?? GRID_TEMPLATE_CLASS[3];

  return (
    <div className="rounded-xl bg-background p-4 flex-1 min-w-0">
      <div className={"grid gap-y-4 pr-2"}>
        <div className={cn("grid grid-cols-3 mr-2", gridClassName)}>
          {metrics.map((metric, index) => (
            <div
              key={metric.label}
              className={cn(getColumnAlignClass(index, metrics.length))}
            >
              <MetricValue {...metric} />
            </div>
          ))}
        </div>
        <Separator className="col-span-full" />
        {isLoading ? null : members.length > 0 ? (
          <ScrollArea className="col-span-full max-h-[210px]">
            <BreakdownTable
              rows={members}
              columns={breakdownColumns}
              gridClassName={gridClassName}
            />
          </ScrollArea>
        ) : (
          <div className="col-span-full flex flex-col items-center justify-center gap-3 py-10 text-center">
            <MonthlySipEmptyStateIcon />
            <span className="text-sm text-muted-foreground">
              Monthly SIP by family member will appear here
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
